import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DateRangePicker, { DateRange, defaultRange } from './DateRangePicker';
import KpiCards from './KpiCards';
import TimeSeriesChart from './TimeSeriesChart';

/**
 * /reports/sources/:id — "everything happening inside this source".
 *
 * A source (the company sending leads in) commonly owns several data bodies,
 * so this page is the roll-up that the per-body drilldown can't give you:
 *
 *   - Contact card    — who to call when the numbers look wrong
 *   - KPI cards       — the source's totals across ALL its bodies
 *   - Volume chart    — same, over time
 *   - By type         — CSV / Webhook / API mix for this source
 *   - Data bodies     — every body they feed, click to drill further
 *   - Why rejected    — door-reject reasons pooled across their bodies
 *
 * :id is a source id, or the literal 'unassigned' for bodies with no source
 * (everything created before sources existed). Account isolation is enforced
 * server-side — the id is validated against the caller's account before any
 * numbers come back.
 */

function fmt(n: number): string {
  return n.toLocaleString();
}
function fmtPct(p: number): string {
  return (p * 100).toFixed(1) + '%';
}

const TYPE_LABELS: Record<string, string> = {
  csv: 'CSV Upload',
  webhook: 'Webhook',
  api: 'API',
  unknown: 'Unknown (pre-rollup data)',
};

const REJECT_REASON_LABELS: Record<string, string> = {
  rate_limit: 'Rate limit',
  concurrency: 'Concurrency limit',
  required_field: 'Missing required field',
  '': 'Unclassified',
};

export default function ReportingSourcePage() {
  const { id } = useParams<{ id: string }>();
  const { organization } = useAuth();
  const tz = organization?.timezone || undefined;
  const [range, setRange] = useState<DateRange>(() => defaultRange(tz));
  const [data, setData] = useState<api.ReportingSourceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (forceFresh = false) => {
    if (!id) {return;}
    setLoading(true);
    setError(null);
    try {
      setData(await api.getReportingSource(id, range.from, range.to, forceFresh));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load source report');
    } finally {
      setLoading(false);
    }
  }, [id, range.from, range.to]);

  useEffect(() => { void load(false); }, [load]);

  const isUnassigned = id === api.UNASSIGNED_SOURCE;
  const title = isUnassigned ? 'Unassigned' : data?.source?.name || 'Source';

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1280, margin: '0 auto' }}>
      <Link to="/reports" style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>
        ← Back to Reporting
      </Link>

      <h1 style={{ margin: '10px 0 4px 0', fontSize: 28, fontWeight: 700, color: '#111827' }}>{title}</h1>
      <p style={{ marginTop: 0, marginBottom: 20, color: '#6b7280', fontSize: 14 }}>
        {isUnassigned
          ? 'Data bodies with no source assigned. Assign one from the body’s settings to move it out of here.'
          : `Everything this source sends, across ${data?.bodies.length ?? 0} data ${(data?.bodies.length ?? 0) === 1 ? 'body' : 'bodies'} in this range.`}
      </p>

      <div style={{ marginBottom: 20 }}>
        <DateRangePicker
          value={range}
          onChange={setRange}
          onRefresh={() => load(true)}
          timezone={tz}
        />
      </div>

      {error && (
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', marginBottom: 20 }}>
          {error}
        </div>
      )}

      {loading && !data && (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
      )}

      {data && (
        <>
          {data.source && <ContactCard source={data.source} />}

          <div style={{ marginBottom: 24 }}>
            <KpiCards totals={data.overview} />
          </div>

          <SectionTitle>Volume over time</SectionTitle>
          <div style={{ marginBottom: 28 }}>
            <TimeSeriesChart data={data.series} tz={tz} />
          </div>

          <SectionTitle>By type</SectionTitle>
          <SimpleBarTable
            rows={data.types.map(t => ({
              key: t.source,
              label: TYPE_LABELS[t.source] || t.source,
              accepts: t.accepts,
              rejects: t.rejects,
            }))}
            empty="No brain traffic from this source in this range."
          />

          <SectionTitle style={{ marginTop: 32 }}>Data bodies</SectionTitle>
          <BodiesTable bodies={data.bodies} />

          <SectionTitle style={{ marginTop: 32 }}>Why leads were rejected at the door</SectionTitle>
          <SimpleBarTable
            rows={data.doorRejects.map(d => ({
              key: d.reason || 'unclassified',
              label: REJECT_REASON_LABELS[d.reason] ?? d.reason,
              accepts: 0,
              rejects: d.rejects,
            }))}
            empty="Nothing bounced at the door for this source in this range."
            hideAccepts
          />
        </>
      )}
    </div>
  );
}

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <h2 style={{
      margin: '0 0 12px 0',
      fontSize: 16,
      fontWeight: 600,
      color: '#374151',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      ...style,
    }}>
      {children}
    </h2>
  );
}

function ContactCard({ source }: { source: api.ReportingSourceResponse['source'] }) {
  if (!source) {return null;}
  const address = [source.addressLine1, source.addressLine2, source.city, source.state, source.postalCode, source.country]
    .map(v => (v || '').trim())
    .filter(Boolean)
    .join(', ');

  const items: Array<[string, string | null]> = [
    ['Contact', source.contactName],
    ['Email', source.contactEmail],
    ['Phone', source.contactPhone],
    ['Address', address || null],
  ];
  const present = items.filter(([, v]) => !!v);

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 28,
      padding: '14px 18px',
      background: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      marginBottom: 20,
      fontSize: 13,
    }}>
      {present.length === 0 ? (
        <span style={{ color: '#9ca3af' }}>
          No contact details on file. <Link to="/sources" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>Add them in Sources →</Link>
        </span>
      ) : (
        <>
          {present.map(([label, value]) => (
            <div key={label}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
              <div style={{ color: '#111827', marginTop: 2 }}>{value}</div>
            </div>
          ))}
          <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
            <Link to="/sources" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>Edit in Sources →</Link>
          </div>
        </>
      )}
    </div>
  );
}

/** Shared accept/reject bar rows — used for both the type split and the reject reasons. */
function SimpleBarTable({
  rows,
  empty,
  hideAccepts,
}: {
  rows: Array<{ key: string; label: string; accepts: number; rejects: number }>;
  empty: string;
  hideAccepts?: boolean;
}) {
  if (rows.length === 0) {
    return (
      <div style={{ padding: 24, background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 8, color: '#9ca3af', textAlign: 'center' }}>
        {empty}
      </div>
    );
  }
  const max = rows.reduce((m, r) => Math.max(m, r.accepts + r.rejects), 0);
  const cols = hideAccepts ? '220px 1fr 110px' : '220px 1fr 110px 110px 110px';
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
      {rows.map((r, i) => {
        const total = r.accepts + r.rejects;
        const acceptPct = total === 0 ? 0 : r.accepts / total;
        const barWidth = max === 0 ? 0 : (total / max) * 100;
        return (
          <div key={r.key} style={{
            display: 'grid',
            gridTemplateColumns: cols,
            alignItems: 'center',
            padding: '12px 16px',
            borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
            fontSize: 14,
          }}>
            <div style={{ fontWeight: 600, color: '#111827' }}>{r.label}</div>
            <div style={{ paddingRight: 16 }}>
              <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${barWidth}%`, height: '100%', background: hideAccepts ? '#b91c1c' : '#2563eb' }} />
              </div>
            </div>
            {!hideAccepts && <div style={{ textAlign: 'right', color: '#1d4ed8' }}>{fmt(r.accepts)}</div>}
            <div style={{ textAlign: 'right', color: '#b91c1c' }}>{fmt(r.rejects)}</div>
            {!hideAccepts && <div style={{ textAlign: 'right', color: '#047857', fontWeight: 600 }}>{fmtPct(acceptPct)}</div>}
          </div>
        );
      })}
    </div>
  );
}

function BodiesTable({ bodies }: { bodies: api.ReportingBodyRow[] }) {
  if (bodies.length === 0) {
    return (
      <div style={{ padding: 24, background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 8, color: '#9ca3af', textAlign: 'center' }}>
        No data body activity for this source in this range.
      </div>
    );
  }
  const cols = '1fr 120px 120px 120px 80px';
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: cols,
        padding: '10px 16px',
        background: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
        fontSize: 11,
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}>
        <div>Data Body</div>
        <div style={{ textAlign: 'right' }}>Accepts</div>
        <div style={{ textAlign: 'right' }}>Rejects</div>
        <div style={{ textAlign: 'right' }}>Accept Rate</div>
        <div />
      </div>
      {bodies.map(b => (
        <div key={b.dataBodyId} style={{
          display: 'grid',
          gridTemplateColumns: cols,
          padding: '12px 16px',
          borderTop: '1px solid #f3f4f6',
          alignItems: 'center',
          fontSize: 14,
        }}>
          <div style={{ fontWeight: 600, color: '#111827' }}>
            {b.dataBodyName || <span style={{ color: '#9ca3af' }}>(deleted body)</span>}
          </div>
          <div style={{ textAlign: 'right', color: '#1d4ed8' }}>{fmt(b.accepts)}</div>
          <div style={{ textAlign: 'right', color: '#b91c1c' }}>{fmt(b.rejects)}</div>
          <div style={{ textAlign: 'right', color: '#047857', fontWeight: 600 }}>{fmtPct(b.acceptRate)}</div>
          <div style={{ textAlign: 'right' }}>
            <Link
              to={`/reports/bodies/${b.dataBodyId}`}
              style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
            >
              Drill →
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}
